import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { publicIdSelector } from '_app/features/session';
import { propertyQueryKeys } from '_app/features/property/shared/propertyQueryKeys';
import Utils from '_app/utils';

import * as propertyApi from '_app/features/property/shared/propertyApi';
import { buildSearchSections, findSearchItem, serializeSearchForm, updateSearchItem } from './searchFormModel';

export const useSearchFormController = () => {
  const history = useHistory();
  const publicId = useSelector(publicIdSelector);
  const queryClient = useQueryClient();
  const [items, setItems] = useState([]);

  const syncSearchForm = useCallback(async () => {
    const nextItems = await propertyApi.fetchSearchForm();
    queryClient.setQueryData(propertyQueryKeys.searchForm, nextItems);
    setItems(nextItems);
  }, [queryClient]);

  const searchFormQuery = useQuery({
    queryKey: propertyQueryKeys.searchForm,
    queryFn: propertyApi.fetchSearchForm,
    enabled: !!publicId,
  });

  useEffect(() => {
    if (searchFormQuery.data) {
      setItems(searchFormQuery.data);
    }
  }, [searchFormQuery.data]);

  const sections = useMemo(() => buildSearchSections(items), [items]);
  const simpleSearch = useMemo(() => findSearchItem(items, 'simple_search'), [items]);

  const updateItem = useCallback((updatedItem) => {
    setItems((currentItems) => updateSearchItem(currentItems, updatedItem));
  }, []);

  const resetMutation = useMutation({
    mutationFn: propertyApi.resetSearchForm,
    onSuccess: async () => {
      await syncSearchForm();
      Utils.showInfoToast('Successfully updated property search.');
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  const applyMutation = useMutation({
    mutationFn: () => propertyApi.submitSearchForm(serializeSearchForm(items)),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.searchForm });
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.collections });
      await queryClient.invalidateQueries({ queryKey: propertyQueryKeys.tabCounts });
      Utils.showInfoToast('Successfully updated property search.');
      history.push(`/${publicId}/listing/new/`);
    },
    onError: (error) => {
      Utils.showErrorToast(Utils.getError(error).message);
    },
  });

  return {
    loading: searchFormQuery.isLoading || resetMutation.isLoading || applyMutation.isLoading,
    sections,
    simpleSearch,
    publicId,
    updateItem,
    onCancel: () => history.push(`/${publicId}/listing/new/`),
    onReset: () => resetMutation.mutate(),
    onApply: () => applyMutation.mutate(),
  };
};
